/**
 * Lightweight SVG Icons Component
 * Replaces heavy @ant-design/icons bundle (~200KB) with inline SVGs (~5KB)
 * 
 * Usage: import { ArrowLeftIcon, SearchIcon } from '@/components/Icons';
 */

import React from 'react';

interface IconProps {
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
  size?: number;
  color?: string;
}

const defaultProps: IconProps = {
  size: 16,
  color: 'currentColor',
};

// Arrow Left - replaces ArrowLeftOutlined
export const ArrowLeftIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M872 474H286.9l350.2-304c5.6-4.9 2.2-14-5.2-14h-88.5c-3.9 0-7.6 1.4-10.5 3.9L155 487.8a31.96 31.96 0 000 48.3L532.9 864c2.9 2.5 6.6 3.9 10.5 3.9h88.5c7.4 0 10.8-9.2 5.2-14L286.9 550H872c4.4 0 8-3.6 8-8v-60c0-4.4-3.6-8-8-8z" />
  </svg>
);

// Arrow Right - replaces ArrowRightOutlined
export const ArrowRightIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h88.5c3.9 0 7.6-1.4 10.5-3.9L869 536.2a32.07 32.07 0 000-48.4z" />
  </svg>
);

// Search - replaces SearchOutlined
export const SearchIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="0 0 1024 1024" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M909.6 854.5L649.9 594.8C690.2 542.7 714 479 714 410c0-186.2-151.8-338-338-338S38 223.8 38 410s151.8 338 338 338c69 0 132.7-23.8 184.8-63.9l259.7 259.7a8.2 8.2 0 0011.6 0l67.5-67.5a8.2 8.2 0 000-11.8zM376 654c-136.5 0-247.5-111-247.5-247.5S239.5 159 376 159s247.5 111 247.5 247.5S512.5 654 376 654z" />
  </svg>
);

// Phone - replaces PhoneOutlined
export const PhoneIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M877.1 238.7L770.6 132.3c-13-13-30.4-20.3-48.8-20.3s-35.8 7.2-48.8 20.3L558.3 246.8c-13 13-20.3 30.5-20.3 48.9 0 18.5 7.2 35.8 20.3 48.9l89.6 89.7a405.46 405.46 0 01-86.4 127.3c-36.7 36.9-79.6 66-127.2 86.6l-89.6-89.7c-13-13-30.4-20.3-48.8-20.3a68.2 68.2 0 00-48.8 20.3L132.3 673c-13 13-20.3 30.5-20.3 48.9 0 18.5 7.2 35.8 20.3 48.9l106.4 106.4c22.2 22.2 52.8 34.9 84.2 34.9 6.5 0 12.8-.5 19.2-1.6 132.4-21.8 263.8-92.3 369.9-198.3C818 606 888.4 474.6 920.2c-6.4 1.1-12.7 1.6-19.2 1.6-31.4 0-62-12.7-84.2-34.9z" />
  </svg>
);

// Mobile - replaces MobileOutlined
export const MobileIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M744 62H280c-35.3 0-64 28.7-64 64v768c0 35.3 28.7 64 64 64h464c35.3 0 64-28.7 64-64V126c0-35.3-28.7-64-64-64zm-8 824H288V134h448v752zM472 784a40 40 0 1080 0 40 40 0 10-80 0z" />
  </svg>
);

// WhatsApp - replaces WhatsAppOutlined
export const WhatsAppIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

// User - replaces UserOutlined
export const UserIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

// Logout - replaces LogoutOutlined
export const LogoutIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M868 732h-70.3c-4.8 0-9.3 2.1-12.3 5.8-7.6 9.2-16.2 17.7-25.6 25.4-21.1 17.3-44.9 31-70.8 40.5-26.8 9.9-55.2 14.9-84.5 14.9-29.2 0-57.6-5-84.4-14.9-25.9-9.6-49.7-23.2-70.8-40.5-21.1-17.3-38.1-37.5-50.6-60-12.9-23.4-21.3-48.3-25-74.4h-70.5c-.9 16.1-1.4 32.4-1.4 48.9v2.5c0 138.5 112.5 251 251 251s251-112.5 251-251c0-27.5-4.4-54-12.6-78.7z" />
    <path d="M561.6 490.2L421.9 629.9c-10.3 10.3-27.1 3-27.1-11.6V512H268c-26.5 0-48-21.5-48-48s21.5-48 48-48h126.8V309.7c0-14.6 16.8-22 27.1-11.6l139.7 139.7c6.4 6.4 6.4 16.8 0 23.2v29.2z" />
  </svg>
);

// Calendar - replaces CalendarOutlined
export const CalendarIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M880 184H712v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H384v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H144c-17.7 0-32 14.3-32 32v664c0 17.7 14.3 32 32 32h736c17.7 0 32-14.3 32-32V216c0-17.7-14.3-32-32-32zm-40 656H184V460h656v380zm0-448H184V256h128v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h256v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h128v136z" />
  </svg>
);

// Environment/Location - replaces EnvironmentOutlined
export const LocationIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M512 327c-29.9 0-58 11.6-79.2 32.8A111.6 111.6 0 00400 439c0 29.9 11.7 58 32.8 79.2A111.6 111.6 0 00512 551c29.9 0 58-11.7 79.2-32.8A111.6 111.6 0 00624 439c0-29.9-11.6-58-32.8-79.2A111.6 111.6 0 00512 327zm342.6-37.9a362.49 362.49 0 00-79.9-115.7 370.83 370.83 0 00-118.2-77.8C610.7 76.6 562.1 67 512 67c-50.1 0-98.7 9.6-144.5 28.5-44.3 18.3-84 44.5-118.2 77.8A363.6 363.6 0 00169.4 289c-19.5 45-29.4 92.8-29.4 142 0 70.6 16.9 140.9 50.1 208.7 26.7 54.5 64 107.6 111 158.1 80.3 86.2 164.5 138.9 188.4 153a43.9 43.9 0 0022.4 6.1c7.8 0 15.5-2 22.4-6.1 23.9-14.1 108.1-66.8 188.4-153 47-50.4 84.3-103.6 111-158.1C867.1 572 884 501.8 884 431.1c0-49.2-9.9-97-29.4-142zM512 615c-97.2 0-176-78.8-176-176s78.8-176 176-176 176 78.8 176 176-78.8 176-176 176z" />
  </svg>
);

// Close Circle - replaces CloseCircleOutlined
export const CloseCircleIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z" />
    <path d="M603.5 500.8l115.3-115.3a8.03 8.03 0 000-11.3l-45-45a8.03 8.03 0 00-11.3 0L547.2 444.5 431.9 329.2a8.03 8.03 0 00-11.3 0l-45 45a8.03 8.03 0 000 11.3l115.3 115.3-115.3 115.3a8.03 8.03 0 000 11.3l45 45a8.03 8.03 0 0011.3 0l115.3-115.3 115.3 115.3a8.03 8.03 0 0011.3 0l45-45a8.03 8.03 0 000-11.3L603.5 500.8z" />
  </svg>
);

// Close - replaces CloseOutlined
export const CloseIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 00203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z" />
  </svg>
);

// Mail - replaces MailOutlined
export const MailIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M928 160H96c-17.7 0-32 14.3-32 32v640c0 17.7 14.3 32 32 32h832c17.7 0 32-14.3 32-32V192c0-17.7-14.3-32-32-32zm-40 110.8V792H136V270.8l-27.6-21.5 39.3-50.5 42.8 33.3h643.1l42.8-33.3 39.3 50.5-27.7 21.5zM833.6 232L512 482 190.4 232l-42.8-33.3-39.3 50.5 27.6 21.5 341.6 265.6a55.99 55.99 0 0068.7 0L888 270.8l27.6-21.5-39.3-50.5-42.7 33.2z" />
  </svg>
);

// Swap (for sort) - replaces SwapOutlined
export const SwapIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M847.9 592H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h605.2L612.9 851c-4.1 5.2-.4 13 6.3 13h72.5c4.9 0 9.5-2.2 12.6-6.1l168.8-214.1c16.5-21 1.6-51.8-25.2-51.8zM872 356H266.8l144.3-183c4.1-5.2.4-13-6.3-13h-72.5c-4.9 0-9.5 2.2-12.6 6.1L151.1 380.2c-16.5 21-1.6 51.8 25.1 51.8h696c4.4 0 8-3.6 8-8v-60c0-4.4-3.6-8-8-8z" />
  </svg>
);

// Filter - replaces FilterOutlined
export const FilterIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M880.1 154H143.9c-24.5 0-39.8 26.7-27.5 48L349 597.4V838c0 17.7 14.2 32 31.8 32h262.4c17.6 0 31.8-14.3 31.8-32V597.4L907.6 202c12.3-21.3-3-48-27.5-48zM603.4 798H420.6V642h182.9v156zm9.6-236.6l-9.5 16.6h-183l-9.5-16.6L212.7 226h598.6L613 561.4z" />
  </svg>
);

// Login - replaces LoginOutlined
export const LoginIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M521.7 82c-152.5-.4-286.7 78.5-363.4 197.7-3.4 5.3.4 12.3 6.7 12.3h70.3c4.8 0 9.3-2.1 12.3-5.8 7.6-9.2 16.2-17.7 25.6-25.4 21.1-17.3 44.9-31 70.8-40.5C370.9 209.9 399.2 205 428 205c29.2 0 57.7 5 84.6 15 26 9.6 49.8 23.2 70.8 40.5 21.1 17.3 38.1 37.5 50.6 60 12.9 23.4 21.3 48.3 25 74.4H728V200.2c0-16-14.6-27.5-30.2-23.9-38.5 8.9-79 30.8-111.1 61.7-32.1-30.9-72.6-52.8-111.1-61.7-15.7-3.6-30.3 7.9-30.3 23.9v194.7h69.4c3.7-26.1 12.1-51 25-74.4 12.5-22.5 29.5-42.7 50.6-60 21-17.3 44.8-30.9 70.8-40.5 26.9-10 55.3-15 84.5-15 29.3 0 57.6 5 84.5 14.9 25.9 9.6 49.8 23.2 70.8 40.5 9.4 7.7 18 16.2 25.6 25.4 3 3.7 7.5 5.8 12.3 5.8h70.3c6.3 0 10.1-7 6.7-12.3C803.4 160.5 669.2 81.6 516.8 82h4.9z" />
    <path d="M521.7 574c-66.9 0-129.1 19.4-181.8 53L339 439.6c.1-31.4-25.4-57-56.8-57H216c-31.4 0-57 25.6-57 57V574c52.7-33.6 114.9-53 181.8-53h4.9c66.9 0 129.1 19.4 181.8 53v-134.4c.1-31.4-25.4-57-56.8-57z" />
  </svg>
);

// Left Arrow (for carousel) - replaces LeftOutlined
export const LeftIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z" />
  </svg>
);

// Right Arrow (for carousel) - replaces RightOutlined
export const RightIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.4z" />
  </svg>
);

// Plus - replaces PlusOutlined
export const PlusIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M482 152h60q8 0 8 8v704q0 8-8 8h-60q-8 0-8-8V160q0-8 8-8z" />
    <path d="M176 474h672q8 0 8 8v60q0 8-8 8H176q-8 0-8-8v-60q0-8 8-8z" />
  </svg>
);

// Minus - replaces MinusOutlined
export const MinusIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M176 474h672q8 0 8 8v60q0 8-8 8H176q-8 0-8-8v-60q0-8 8-8z" />
  </svg>
);

// Clock - replaces ClockCircleOutlined
export const ClockIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z" />
    <path d="M686.7 638.6L544.1 535.5V288c0-4.4-3.6-8-8-8H488c-4.4 0-8 3.6-8 8v275.4c0 2.6 1.2 5 3.3 6.5l165.4 120.6c3.6 2.6 8.6 1.8 11.2-1.7l28.6-39c2.6-3.7 1.8-8.7-1.8-11.2z" />
  </svg>
);

// Down Arrow (dropdown) - replaces DownOutlined
export const DownIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M884 256h-75c-5.1 0-9.9 2.5-12.9 6.6L512 654.2 227.9 262.6c-3-4.1-7.8-6.6-12.9-6.6h-75c-6.5 0-10.3 7.4-6.5 12.7l352.6 486.1c12.8 17.6 39 17.6 51.7 0l352.6-486.1c3.9-5.3.1-12.7-6.4-12.7z" />
  </svg>
);

// Social Media Icons
export const InstagramIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

export const LinkedInIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
  </svg>
);

export const GoogleBusinessIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z" />
  </svg>
);

// Info Icon - for About page
export const InfoIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm32 664c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V456c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v272zm-32-344a48.01 48.01 0 010-96 48.01 48.01 0 010 96z" />
  </svg>
);

// Shield Icon - for Privacy Policy
export const ShieldIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M866.9 169.9L527.1 54.1C523 52.7 517.5 52 512 52s-11 .7-15.1 2.1L157.1 169.9c-8.3 2.8-15.1 12.4-15.1 21.2v482.4c0 8.8 5.9 19.4 14.5 22.2l365.4 125.7c10.7 3.7 21.1 3.7 31.8 0L865 695.6c8.5-2.8 14.5-13.4 14.5-22.2V191.1c.2-8.8-6.6-18.3-14.6-21.2zM810 654.3L512 763.9 214 654.3V226.7l298 101.6 298-101.6v427.6z" />
  </svg>
);

// Document Icon - for Terms & Conditions
export const DocumentIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M854.6 288.6L639.4 73.4c-6-6-14.1-9.4-22.6-9.4H192c-17.7 0-32 14.3-32 32v832c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V311.3c0-8.5-3.4-16.7-9.4-22.7zM790.2 326H602V137.8L790.2 326zm1.8 562H232V136h302v240a42 42 0 0042 42h240v494z" />
  </svg>
);

// Check Circle - replaces CheckCircleOutlined
export const CheckCircleIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm193.5 301.7l-210.6 292a31.8 31.8 0 01-51.7 0L318.5 484.9c-3.8-5.3 0-12.7 6.5-12.7h46.9c10.2 0 19.9 4.9 25.9 13.3l71.2 98.8 157.2-218c6-8.3 15.6-13.3 25.9-13.3H699c6.5 0 10.3 7.4 6.5 12.7z" />
  </svg>
);

// Home - replaces HomeOutlined
export const HomeIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M946.5 505L560.1 118.8l-25.9-25.9a31.5 31.5 0 00-44.4 0L77.5 505a63.9 63.9 0 00-18.8 46c.4 35.2 29.7 63.3 65 63.3h42.5V940h691.8V614.3h43.4c17.1 0 33.2-6.7 45.3-18.8a63.6 63.6 0 0018.7-45.3c0-17-6.7-33.1-18.8-45.2zM568 868H456V664h112v204zm217.9-325.7V868H632V640c0-22.1-17.9-40-40-40H432c-22.1 0-40 17.9-40 40v228H238.1V542.3h-96l370-369.7 23.1 23.1L882 542.3h-96.1z" />
  </svg>
);

// QR Code - replaces QrcodeOutlined
export const QrcodeIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M136 384h56c4.4 0 8-3.6 8-8V200h176c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8H196c-37.6 0-68 30.4-68 68v180c0 4.4 3.6 8 8 8zm584-184h176v176c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8V196c0-37.6-30.4-68-68-68H728c-4.4 0-8 3.6-8 8v56c0 4.4 3.6 8 8 8zM728 888H196c-37.6 0-68-30.4-68-68V640c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v180c0 37.6 30.4 68 68 68h532c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8zm0-104H544c-4.4 0-8 3.6-8 8v56c0 4.4 3.6 8 8 8h184v176c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8V640c0-4.4-3.6-8-8-8zM304 240h112v112H304V240zm0 432h112v112H304V672zm432-432h112v112H736V240zM240 240h112v112H240V240zm0 432h112v112H240V672zm432-432h112v112H672V240zm0 432h112v112H672V672z" />
  </svg>
);

// Smile - replaces SmileOutlined
export const SmileIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z" />
    <path d="M288 421a48.01 48.01 0 1096 0 48.01 48.01 0 10-96 0zm224 112c-85.5 0-155.6 67.3-160 151.6a8 8 0 008 8.4h48c4.4 0 7.9-3.5 8-7.8 4-60.9 54.9-109.9 116-109.9s112 49 116 109.9c.1 4.3 3.6 7.8 8 7.8h48a8 8 0 008-8.4C667.6 600.3 597.5 533 512 533zm216-112a48.01 48.01 0 1096 0 48.01 48.01 0 10-96 0z" />
  </svg>
);

// Trophy - replaces TrophyOutlined
export const TrophyIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M868 160h-92v-40c0-4.4-3.6-8-8-8H256c-4.4 0-8 3.6-8 8v40h-92a44 44 0 00-44 44v148c0 81.7 60 149.6 138.2 162 19.9 77.5 76.1 141.3 146.8 170.5-19.8 20.1-33.1 44.9-38.4 72.9-20.3 4.1-36.6 20.6-36.6 41.9v27.1c0 24.4 19.7 44.1 44.1 44.1h420.8c24.4 0 44.1-19.7 44.1-44.1V884c0-21.3-16.3-37.8-36.6-41.9-5.3-28-18.6-52.8-38.4-72.9 70.7-29.2 126.9-93 146.8-170.5C812 503.6 872 435.7 872 354V204a44 44 0 00-44-44zM184 354V204h100v83.6C218.5 282 184 315.9 184 354zm520 134c0 77.1-65.1 139.7-144.2 141-78.1-1.3-143.8-63.9-143.8-141v-68.7c60.2-20.1 110.8-55.9 144.2-97.1 33.4 41.2 84 77 144.2 97.1V488zm200-134c0-38.1-34.5-72-80-66.4V204h100v150z" />
  </svg>
);

// Form/File - replaces FormOutlined
export const FormIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M854.6 288.6L639.4 73.4c-6-6-14.1-9.4-22.6-9.4H192c-17.7 0-32 14.3-32 32v832c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V311.3c0-8.5-3.4-16.7-9.4-22.7zM790.2 326H602V137.8L790.2 326zm1.8 562H232V136h302v240a42 42 0 0042 42h240v494zM504 618H320c-4.4 0-8 3.6-8 8v48c0 4.4 3.6 8 8 8h184c4.4 0 8-3.6 8-8v-48c0-4.4-3.6-8-8-8zM312 490v48c0 4.4 3.6 8 8 8h384c4.4 0 8-3.6 8-8v-48c0-4.4-3.6-8-8-8H320c-4.4 0-8 3.6-8 8z" />
  </svg>
);

// Star - replaces StarOutlined
export const StarIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="64 64 896 896" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M908.1 353.1l-253.9-36.9L540.7 86.1c-3.1-6.3-8.2-11.4-14.5-14.5-15.8-7.8-35-1.3-42.9 14.5L369.8 316.2l-253.9 36.9c-7 1-13.4 4.3-18.3 9.3a32.05 32.05 0 00.6 45.3l183.7 179-43.4 252.9a31.95 31.95 0 0046.4 33.7L512 754l227.1 119.4c6.2 3.3 13.4 4.4 20.3 3.2 17.4-3 29.1-19.5 26.1-36.9l-43.4-252.9 183.7-179c5-4.9 8.3-11.3 9.3-18.3 2.7-17.5-9.5-33.7-27-36.3zM664.8 561.6l36.1 210.3L512 672.7 323.1 772l36.1-210.3-152.8-149L417.6 382 512 190.7 606.4 382l211.2 30.7-152.8 148.9z" />
  </svg>
);

// Coffee - replaces faCoffee
export const CoffeeIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="0 0 640 512" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M192 384h192c53 0 96-43 96-96h32c70.6 0 128-57.4 128-128S582.6 32 512 32H120c-13.3 0-24 10.7-24 24v232c0 53 43 96 96 96zM512 96c35.3 0 64 28.7 64 64s-28.7 64-64 64h-32V96h32zm47.7 384H48.3c-47.6 0-61-64-36-64h583.3c25 0 11.8 64-35.9 64z" />
  </svg>
);

// Cloud Sun - replaces faCloudSun (covered)
export const CloudSunIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="0 0 640 512" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M576 224c-11.1 0-22.1 1.5-32.4 4.2C525.1 189.1 487.6 160 444.4 160c-12.9 0-25.1 2.8-36.3 7.7C389.7 124.9 343.9 96 292.7 96c-70.7 0-128 57.3-128 128 0 4.8.5 9.5 1.4 14.1C108.2 245.9 64 290.8 64 344c0 54.4 44.1 98.5 98.5 98.5H576c35.3 0 64-28.7 64-64s-28.7-64-64-64zm-96-96c0-35.3 28.7-64 64-64s64 28.7 64 64-28.7 64-64 64-64-28.7-64-64z" />
  </svg>
);

// Cloud Sun Rain - replaces faCloudSunRain (uncovered)
export const UncoveredIcon: React.FC<IconProps> = ({
  size = 18,
  color = 'currentColor',
  className,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M3 15a4 4 0 014-4h1a5 5 0 019.7 1" />
    <path d="M16 16h1a3 3 0 010 6H7a4 4 0 010-8" />
    <circle cx="6" cy="6" r="2" />
  </svg>
);


// Handshake - replaces faHandshake (partnered)
export const PartneredIcon: React.FC<IconProps> = ({
  size = 18,
  color = 'currentColor',
  className,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M8 12l2 2 4-4" />
    <path d="M2 12l6-6 4 4" />
    <path d="M22 12l-6-6-4 4" />
  </svg>
);


// Arrow Circle Up - replaces faArrowCircleUp (price low to high)
export const ArrowCircleUpIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="0 0 512 512" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256 256-114.6 256-256S397.4 0 256 0zm0 464c-114.7 0-208-93.3-208-208S141.3 48 256 48s208 93.3 208 208-93.3 208-208 208zm133.6-301.1c-6.2-6.2-16.4-6.2-22.6 0L256 234.7 145.1 123.8c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6L233.4 256 122.5 366.9c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0L256 277.3l110.9 110.9c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6L278.6 256l110.9-110.9c6.3-6.2 6.3-16.4.1-22.6z" />
  </svg>
);

// Arrow Circle Down - replaces faArrowCircleDown (price high to low)
export const ArrowCircleDownIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="0 0 512 512" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256 256-114.6 256-256S397.4 0 256 0zm0 464c-114.7 0-208-93.3-208-208S141.3 48 256 48s208 93.3 208 208-93.3 208-208 208zm133.6-301.1c-6.2-6.2-16.4-6.2-22.6 0L256 234.7 145.1 123.8c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6L233.4 256 122.5 366.9c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0L256 277.3l110.9 110.9c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6L278.6 256l110.9-110.9c6.3-6.2 6.3-16.4.1-22.6z" />
  </svg>
);

// Circle Chevron Up - replaces faCircleChevronUp
export const CircleChevronUpIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="0 0 512 512" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256 256-114.6 256-256S397.4 0 256 0zm0 464c-114.7 0-208-93.3-208-208S141.3 48 256 48s208 93.3 208 208-93.3 208-208 208zm133.6-301.1c-6.2-6.2-16.4-6.2-22.6 0L256 234.7 145.1 123.8c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6L233.4 256 122.5 366.9c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0L256 277.3l110.9 110.9c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6L278.6 256l110.9-110.9c6.3-6.2 6.3-16.4.1-22.6z" />
  </svg>
);

// Route - replaces faRoute
export const RouteIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="0 0 512 512" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M416 320h-96c-17.67 0-32-14.33-32-32s14.33-32 32-32h96c17.67 0 32-14.33 32-32s-14.33-32-32-32h-96c-53.02 0-96 42.98-96 96s42.98 96 96 96h96c17.67 0 32-14.33 32-32s-14.33-32-32-32zm-192-128h96c17.67 0 32-14.33 32-32s-14.33-32-32-32h-96c-17.67 0-32 14.33-32 32s14.33 32 32 32zm0 64h-96c-53.02 0-96-42.98-96-96s42.98-96 96-96h96c17.67 0 32-14.33 32-32s-14.33-32-32-32h-96c-88.22 0-160 71.78-160 160s71.78 160 160 160h96c17.67 0 32-14.33 32-32s-14.33-32-32-32z" />
  </svg>
);

// Up Down - replaces faUpDown
export const UpDownIcon: React.FC<IconProps> = ({ 
  className, style, onClick, size = 16, color = 'currentColor' 
}) => (
  <svg 
    viewBox="0 0 320 512" 
    width={size} 
    height={size} 
    fill={color}
    className={className}
    style={style}
    onClick={onClick}
    aria-hidden="true"
  >
    <path d="M177 255.7l136 136c9.4 9.4 9.4 24.6 0 33.9l-22.6 22.6c-9.4 9.4-24.6 9.4-33.9 0L160 351.9l-96.4 96.4c-9.4 9.4-24.6 9.4-33.9 0L7 425.7c-9.4-9.4-9.4-24.6 0-33.9l136-136c9.4-9.5 24.6-9.5 34-.1zm34-192L7 95.7c-9.4-9.4-9.4-24.6 0-33.9L29.7 39.2c9.4-9.4 24.6-9.4 33.9 0L160 128.1l96.4-96.4c9.4-9.4 24.6-9.4 33.9 0L313 61.8c9.4 9.4 9.4 24.6 0 33.9l-136 136c-9.4 9.5-24.6 9.5-34 .1z" />
  </svg>
);

// Football Ball - replaces faFootballBall
export const FootballIcon: React.FC<IconProps> = ({
  size = 18,
  color = 'currentColor',
  className,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="9" />
    <polygon points="12 7 15 9 14 13 10 13 9 9" />
  </svg>
);


// Export all icons as a namespace for convenience
export const Icons = {
  ArrowLeft: ArrowLeftIcon,
  ArrowRight: ArrowRightIcon,
  Search: SearchIcon,
  Phone: PhoneIcon,
  Mobile: MobileIcon,
  WhatsApp: WhatsAppIcon,
  User: UserIcon,
  Logout: LogoutIcon,
  Calendar: CalendarIcon,
  Clock: ClockIcon,
  Location: LocationIcon,
  CloseCircle: CloseCircleIcon,
  Close: CloseIcon,
  Mail: MailIcon,
  Swap: SwapIcon,
  Filter: FilterIcon,
  Login: LoginIcon,
  Left: LeftIcon,
  Right: RightIcon,
  Plus: PlusIcon,
  Minus: MinusIcon,
  Down: DownIcon,
  Instagram: InstagramIcon,
  LinkedIn: LinkedInIcon,
  GoogleBusiness: GoogleBusinessIcon,
  Info: InfoIcon,
  Shield: ShieldIcon,
  Document: DocumentIcon,
  CheckCircle: CheckCircleIcon,
  Home: HomeIcon,
  Qrcode: QrcodeIcon,
  Smile: SmileIcon,
  Trophy: TrophyIcon,
  Form: FormIcon,
  Star: StarIcon,
  Coffee: CoffeeIcon,
  CloudSun: CloudSunIcon,
  CloudSunRain: UncoveredIcon,
  Handshake: PartneredIcon,
  ArrowCircleUp: ArrowCircleUpIcon,
  ArrowCircleDown: ArrowCircleDownIcon,
  CircleChevronUp: CircleChevronUpIcon,
  Route: RouteIcon,
  UpDown: UpDownIcon,
  FootballBall: FootballIcon,
};

export default Icons;
